import React from 'react';
import { Link } from 'react-router-dom';
import { documentsAPI } from '../api/client';
import { recordRight } from '../auth/recordRights';

/**
 * The Policy Area cell (issue #61), which replaced a "📎 Attached / No file"
 * column derived from the LEGACY single-file `Document.file_path` -- a field
 * DocumentForm.js stopped writing when multi-file DocumentAttachments landed,
 * so for anything authored since then it read "No file" regardless.
 *
 * A document may carry any number of areas. Only the first two are named, so a
 * document tagged with a dozen cannot stretch the row; the true count stays
 * visible as "+N more". Nothing is fetched for this -- policy_areas has ridden
 * on every DocumentResponse since long before this change.
 */
const MAX_SHOWN_POLICY_AREAS = 2;

function renderPolicyAreas(areas) {
  if (!areas || areas.length === 0) {
    return <span style={{ color: 'var(--muted-light)', fontSize: '12px' }}>—</span>;
  }
  const shown = areas.slice(0, MAX_SHOWN_POLICY_AREAS);
  const overflow = areas.length - shown.length;
  return (
    <span style={{ fontSize: '13px' }}>
      {shown.map(a => a.name).join(', ')}
      {overflow > 0 && (
        <span style={{ color: 'var(--muted)', fontSize: '12px' }}> +{overflow} more</span>
      )}
    </span>
  );
}

function DocumentList({
  documents, nodes, onDelete, onRefresh, onSubmitForApproval, onStartReview,
  canUpdate = false, canDelete = false, scope, onShowAllLocations,
}) {
  const getNodeName = (nodeId) => {
    const node = nodes.find(n => n.id === nodeId);
    return node ? node.name : `Node #${nodeId}`;
  };

  const getStatusBadge = (status) => {
    return <span className={`badge badge-${status}`}>{status}</span>;
  };

  const handleDownload = async (docId, title) => {
    try {
      const response = await documentsAPI.download(docId);
      
      // Create blob and download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${title}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      alert('Failed to download file');
      console.error('Download error:', error);
    }
  };

  if (documents.length === 0) {
    // scope='mine' returning nothing is a different situation from a genuinely
    // empty corpus (issue #17): the documents almost certainly exist, just
    // outside this viewer's default locations, so the fix is one click away
    // rather than "go create something."
    if (scope === 'mine' && onShowAllLocations) {
      return (
        <div className="card">
          <div className="empty-state">
            <h3>No documents in your locations</h3>
            <p>Nothing matched your default locations and the current filters.</p>
            <button className="btn btn-secondary" onClick={onShowAllLocations}>
              🌐 Show all locations
            </button>
          </div>
        </div>
      );
    }
    return (
      <div className="card">
        <div className="empty-state">
          <h3>No documents found</h3>
          <p>Create your first document to get started!</p>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-header">
        <h3>Documents ({documents.length})</h3>
      </div>
      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>Title</th>
              <th>Hierarchy</th>
              <th>Version</th>
              <th>Status</th>
              <th>Policy Area</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {documents.map(doc => (
              <tr key={doc.id}>
                <td><Link to={`/documents/${doc.id}`}><strong>{doc.title}</strong></Link></td>
                <td>{getNodeName(doc.hierarchy_node_id)}</td>
                <td>
                  {doc.version_label}
                  {/* The policy is still ACTIVE and still enforced -- this is a
                      proposal in flight, not a status change. */}
                  {doc.open_revision_id && (
                    <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '2px' }}>
                      ↻ {doc.open_revision_purpose === 'periodic_review'
                        ? 'review in progress'
                        : `${doc.open_revision_version_label} in progress`}
                    </div>
                  )}
                </td>
                <td>{getStatusBadge(doc.status)}</td>
                <td>{renderPolicyAreas(doc.policy_areas)}</td>
                <td>{new Date(doc.created_at).toLocaleDateString()}</td>
                <td>
                  {/* .btn-group (global.css) rather than per-button marginRight --
                      gives every pair here proper spacing and lets the row wrap
                      instead of the buttons visually bleeding into each other
                      (GitHub issue #163). */}
                  <div className="btn-group" style={{ marginBottom: 0 }}>
                    {doc.file_path && (
                      <button
                        className="btn btn-primary btn-small"
                        onClick={() => handleDownload(doc.id, doc.title)}
                      >
                        ⬇️ Download
                      </button>
                    )}
                    {recordRight(doc.can_edit, canUpdate) && doc.status === 'draft' && (
                      <button
                        className="btn btn-success btn-small"
                        onClick={() => onSubmitForApproval(doc)}
                      >
                        {doc.pending_workflow_instance_id ? '📤 Resume Submission' : '📤 Submit for Approval'}
                      </button>
                    )}
                    {recordRight(doc.can_edit, canUpdate) && doc.status === 'active' && !doc.open_revision_id && onStartReview && (
                      <button
                        className="btn btn-secondary btn-small"
                        onClick={() => onStartReview(doc)}
                      >
                        🔄 Start Review
                      </button>
                    )}
                    {recordRight(doc.can_delete, canDelete) && (
                      <button
                        className="btn btn-danger btn-small"
                        onClick={() => onDelete(doc.id)}
                      >
                        🗑️ Archive
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default DocumentList;
